import { Copy } from '@carbon/icons-react';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { copyText } from '@/shared/lib/clipboard';
import { cx } from '@/shared/lib/cx';
import s from './Display.module.css';
import { IconButton } from './IconButton';

export interface KeyValueItem {
  id: string;
  term: ReactNode;
  value: ReactNode;
  /** Текст для кнопки «Скопировать» (реквизиты, коды). */
  copyValue?: string;
}

export interface KeyValueProps {
  items: ReadonlyArray<KeyValueItem>;
  layout?: 'columns' | 'stacked';
  className?: string;
}

/** Пары «название — значение» (реквизиты клиники, данные профиля) в списке определений. */
export function KeyValue({ items, layout = 'columns', className }: KeyValueProps) {
  return (
    <dl className={cx(s.kv, layout === 'stacked' && s.kvStacked, className)}>
      {items.map((item) => (
        <div key={item.id} className={s.kvRow}>
          <dt className={s.kvTerm}>{item.term}</dt>
          <dd className={s.kvValue}>
            <KeyValueContent item={item} />
          </dd>
        </div>
      ))}
    </dl>
  );
}

function KeyValueContent({ item }: { item: KeyValueItem }) {
  const { t } = useTranslation('ui');
  const [copied, setCopied] = useState(false);
  const empty = item.value === null || item.value === undefined || item.value === '';
  return (
    <>
      {empty ? (
        <span className={s.kvEmpty}>
          <span aria-hidden="true">—</span>
          <span className="visually-hidden">{t('notSpecified')}</span>
        </span>
      ) : (
        <span>{item.value}</span>
      )}
      {item.copyValue && !empty ? (
        <IconButton
          className={s.kvCopy}
          label={copied ? t('copied') : t('copy')}
          icon={Copy}
          size="sm"
          iconSize={16}
          onClick={async () => setCopied(await copyText(item.copyValue ?? ''))}
        />
      ) : null}
    </>
  );
}
